// A voice message (AttachmentViews.swift VoiceNoteCardView; Android VoiceNoteCard): play/pause,
// back 5 s, a draggable progress bar with elapsed/total clocks under it, the tap-to-cycle speed
// chip, forward 5 s — the same 360-wide raised card. No waveform: the apps draw none.
//
// Also the Files tab's compact play button (VoiceRowButton): the apps' Files sheet has no player,
// but a browser has no Quick Look to fall back on, so a note there plays in place, through the
// same one-at-a-time player.
import { memo } from 'react';
import { AttachmentKind } from '../../../core/attachmentKind';
import { VoiceNoteSeek } from '../../../core/voiceNoteControls';
import { t } from '../../../i18n/i18n';
import { rateLabel } from './voiceFormat';
import { useVoicePlayback, voicePlayback } from './playback';
import './voice.css';

export function PlayGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path d="M7 4.6v14.8a1 1 0 0 0 1.52.85l12-7.4a1 1 0 0 0 0-1.7l-12-7.4A1 1 0 0 0 7 4.6Z" fill="currentColor" />
    </svg>
  );
}

export function PauseGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <rect x="5.5" y="4" width="4.6" height="16" rx="1.4" fill="currentColor" />
      <rect x="13.9" y="4" width="4.6" height="16" rx="1.4" fill="currentColor" />
    </svg>
  );
}

/** SF Symbols' gobackward.5 / goforward.5: an open circle with an arrowhead and the 5 inside. */
function SkipGlyph({ forward }: { forward: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden>
      <g transform={forward ? 'matrix(-1 0 0 1 24 0)' : undefined} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.6 9.2A8 8 0 1 1 4 12.6" />
        <path d="M4.2 4.6v4.9h4.9" />
      </g>
      <text x="12" y="15.4" textAnchor="middle" fontSize="8.4" fontWeight="700" fill="currentColor">
        5
      </text>
    </svg>
  );
}

export interface VoiceFile {
  fileId: string;
  name: string;
  mimeType: string;
}

export const VoiceNoteCard = memo(function VoiceNoteCard({ file, uploading }: { file: VoiceFile; uploading?: number | null }) {
  const playback = useVoicePlayback();
  const mine = playback.note?.fileId === file.fileId ? playback.note : null;
  const ready = !!mine && !mine.loading;
  const position = mine?.position ?? 0;
  const duration = mine?.duration ?? 0;
  const fraction = duration > 0 ? Math.min(1, position / duration) : 0;
  const failed = !mine && playback.failedId === file.fileId;
  const playing = !!mine?.playing;
  const label = ready ? `${AttachmentKind.clock(position)} / ${AttachmentKind.clock(duration)}` : failed ? t('web.voice.playFailed') : t('channel.voiceNote');
  const canBack = ready && VoiceNoteSeek.canStepBack(position, duration);
  const canForward = ready && VoiceNoteSeek.canStepForward(position, duration);
  return (
    <div className="voice-card" title={file.name} data-voice-file={file.fileId}>
      <button
        className="voice-play"
        aria-label={playing ? t('web.voice.pause') : t('web.voice.play')}
        title={playing ? t('web.voice.pause') : t('web.voice.play')}
        onClick={() => voicePlayback.toggle(file.fileId, file.mimeType)}
      >
        {mine?.loading ? <span className="voice-spinner" aria-hidden /> : playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>
      <button className="voice-skip" disabled={!canBack} aria-label={t('channel.voiceSkipBack')} title={t('channel.voiceSkipBack')} onClick={() => voicePlayback.skip(-VoiceNoteSeek.step)}>
        <SkipGlyph forward={false} />
      </button>
      <div className="voice-middle">
        <input
          className="voice-seek"
          type="range"
          min={0}
          max={1000}
          step={1}
          value={Math.round(fraction * 1000)}
          disabled={!ready}
          aria-label={t('web.voice.seek')}
          aria-valuetext={label}
          style={{ ['--voice-progress' as string]: `${fraction * 100}%` }}
          onChange={(e) => voicePlayback.seek(Number(e.target.value) / 1000)}
        />
        <div className="voice-under">
          <span className={`voice-clock${failed ? ' failed' : ''}`}>{label}</span>
          <button className={`voice-rate${mine ? ' live' : ''}`} aria-label={t('web.voice.rate')} title={t('web.voice.rate')} onClick={() => voicePlayback.cycleRate()}>
            {rateLabel(mine?.rate ?? playback.rate)}
          </button>
        </div>
      </div>
      <button className="voice-skip" disabled={!canForward} aria-label={t('channel.voiceSkipForward')} title={t('channel.voiceSkipForward')} onClick={() => voicePlayback.skip(VoiceNoteSeek.step)}>
        <SkipGlyph forward />
      </button>
      {uploading != null && uploading >= 0 && uploading < 1 ? (
        <span className="voice-upload" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(uploading * 100)}>
          <i style={{ width: `${Math.max(4, uploading * 100)}%` }} />
        </span>
      ) : null}
    </div>
  );
});

/** The Files tab's play button: a 36 px accent circle with the note's progress as a ring. */
export function VoiceRowButton({ file }: { file: VoiceFile }) {
  const playback = useVoicePlayback();
  const mine = playback.note?.fileId === file.fileId ? playback.note : null;
  const fraction = mine && mine.duration > 0 ? Math.min(1, mine.position / mine.duration) : 0;
  const playing = !!mine?.playing;
  const r = 16.5;
  const length = 2 * Math.PI * r;
  return (
    <button
      className="voice-row-play"
      aria-label={playing ? t('web.voice.pause') : t('web.voice.play')}
      title={mine && mine.duration > 0 ? `${AttachmentKind.clock(mine.position)} / ${AttachmentKind.clock(mine.duration)}` : file.name}
      onClick={() => voicePlayback.toggle(file.fileId, file.mimeType)}
    >
      <svg className="voice-row-ring" width={36} height={36} viewBox="0 0 36 36" aria-hidden>
        <circle cx="18" cy="18" r={r} fill="none" strokeWidth="2" className="track" />
        <circle cx="18" cy="18" r={r} fill="none" strokeWidth="2" className="fill" strokeDasharray={length} strokeDashoffset={length * (1 - fraction)} transform="rotate(-90 18 18)" />
      </svg>
      <span className="voice-row-glyph">{mine?.loading ? <span className="voice-spinner" aria-hidden /> : playing ? <PauseGlyph size={13} /> : <PlayGlyph size={13} />}</span>
    </button>
  );
}
